/**
 * Notification category mapping and utilities
 * Groups notification sources into higher-level categories for better organization
 *
 * Optimization findings are handled by a PREFIX RULE, not by per-slug entries. The backend's WARNING
 * sink writes `source = "opt:<slug>"` (backend/app/optimization_engine/framework/digest.py), and the
 * registry is meant to grow to 20+ optimizations. A hand-written entry per slug would mean every new
 * detector silently lands in the catch-all 'System' category under its raw slug until someone
 * remembered to edit this file — which is exactly the bug this rule replaces. Anything `opt:`-prefixed
 * is 'Account Health' and gets a humanised display name, so a new detector needs ZERO frontend edits.
 * They are deliberately NOT mapped to 'Recommendations': 7 of the 8 registered detectors emit
 * OutputType.WARNING and only search_term_mining is a RECOMMENDATION, so that would trade one
 * mislabel for another.
 */

export type NotificationCategory = 'Reports' | 'Recommendations' | 'Account Health' | 'Campaign' | 'System';

const OPTIMIZATION_SOURCE_PREFIX = 'opt:';

// Mapping from notification source to category
const SOURCE_TO_CATEGORY: Record<string, NotificationCategory> = {
  // Reports
  'WEEKLY_PERFORMANCE_REPORT': 'Reports',
  'daily_summary': 'Reports',
  'strategic_report': 'Reports',
  'intelligence_report': 'Reports',

  // Recommendations
  'recommendations': 'Recommendations',
  'v0_recommendations': 'Recommendations',
  'v1_recommendations': 'Recommendations',

  // Campaign
  'CAMPAIGN_CREATION': 'Campaign',
  'googleads_campaign_sync': 'Campaign',
  'ad_sync': 'Campaign',
  'keyword_sync': 'Campaign',
  'onboarding': 'Campaign',

  // Account Health: connection alerts, account-independent so every view shows them (#6612)
  'google_connection': 'Account Health',
  'meta_token_refresh': 'Account Health',

  // System
  'ACTIONS': 'System',
  'CONFIGURATION': 'System',
};

// Display names for categories
export const CATEGORY_DISPLAY_NAMES: Record<NotificationCategory, string> = {
  'Reports': 'Reports',
  'Recommendations': 'Recommendations',
  'Account Health': 'Account Health',
  'Campaign': 'Campaign',
  'System': 'System',
};

// Display names for sources
export const SOURCE_DISPLAY_NAMES: Record<string, string> = {
  'WEEKLY_PERFORMANCE_REPORT': 'Weekly Reports',
  'daily_summary': 'Daily Summary',
  'strategic_report': 'Google Ads Report',
  'intelligence_report': 'Intelligence Report',
  'recommendations': 'Recommendations',
  'v0_recommendations': 'V0 Recommendations',
  'v1_recommendations': 'V1 Recommendations',
  'CAMPAIGN_CREATION': 'Campaign Creation',
  'googleads_campaign_sync': 'Google Ads Campaign Sync',
  'ad_sync': 'Ad Sync',
  'keyword_sync': 'Keyword Sync',
  'onboarding': 'Onboarding',
  'google_connection': 'Google Ads Connection',
  'meta_token_refresh': 'Meta Connection',
  'ACTIONS': 'Actions',
  'CONFIGURATION': 'Configuration',
};

/**
 * Optional per-slug overrides for optimizations whose humanised slug reads badly.
 * The humaniser is the default — a new slug only belongs here if it needs better copy.
 */
export const OPTIMIZATION_SLUG_DISPLAY_NAMES: Record<string, string> = {
  'billing_suspended': 'Account suspended or closed',
  'conversion_tracking_missing': 'Conversion tracking not set up',
  'conversion_tracking_inactive': 'Conversion tracking has stopped',
  'account_not_spending': 'Account has stopped spending',
  'google_auto_apply_enabled': 'Google is auto-applying changes',
  'disapproved_ads': 'Ads blocked by Google policy',
  'zero_conversion_drop': 'Campaign suddenly stopped converting',
  'search_term_mining': 'Money wasted on irrelevant searches',
  'presence_or_interest_targeting': 'Location targeting includes people outside the area',
  'landing_page_experience_below_average': 'Landing page rated below average',
  'enhanced_conversions_off': 'Enhanced conversions are off',
  'offline_conversion_uploads_failing': 'CRM conversion uploads are failing',
  'budget_underspending': 'Campaign not spending its budget',
  'sitelinks_disapproved': 'Sitelinks blocked by Google policy',
  'google_tag_missing': 'Landing page has no Google tag',
  'consent_mode_missing': 'Consent mode not found on landing page',
  'landing_page_slow': 'Landing page is slow on mobile',
  'cpa_above_target': 'Cost per conversion above target',
  'budget_constrained': 'Budget holding a campaign back',
  'brand_leak': 'Brand traffic leaking between campaigns',
};

// Order in which categories should be displayed
export const CATEGORY_ORDER: NotificationCategory[] = [
  'Reports',
  'Recommendations',
  'Account Health',
  'Campaign',
  'System',
];

function isOptimizationSource(source: string): boolean {
  return source.startsWith(OPTIMIZATION_SOURCE_PREFIX);
}

/**
 * Turn an optimization slug into readable copy: `billing_suspended` → "Billing suspended".
 * Sentence case, matching the notification titles the findings themselves carry.
 */
function humaniseOptimizationSlug(slug: string): string {
  const words = slug.split('_').filter((word) => word.length > 0);
  if (words.length === 0) {
    return '';
  }
  const [first, ...rest] = words;
  return [first.charAt(0).toUpperCase() + first.slice(1).toLowerCase(), ...rest.map((word) => word.toLowerCase())].join(
    ' ',
  );
}

/**
 * Title-case an unmapped non-optimization source. Preserves exactly what the three inbox surfaces
 * did inline before they were pointed at this module, so no existing label changes.
 */
function titleCaseSource(source: string): string {
  return source
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Get the category for a given notification source
 * Every `opt:`-prefixed source is an optimization finding; unknown sources default to 'System'
 */
export function getCategoryForSource(source: string): NotificationCategory {
  if (isOptimizationSource(source)) {
    return 'Account Health';
  }
  return SOURCE_TO_CATEGORY[source] || 'System';
}

/**
 * Get display name for a category
 */
export function getCategoryDisplayName(category: NotificationCategory): string {
  return CATEGORY_DISPLAY_NAMES[category];
}

/**
 * Get display name for a source
 * Optimization findings are humanised from their slug; other sources fall back to the source itself
 */
export function getSourceDisplayName(source: string): string {
  if (isOptimizationSource(source)) {
    const slug = source.slice(OPTIMIZATION_SOURCE_PREFIX.length);
    return OPTIMIZATION_SLUG_DISPLAY_NAMES[slug] || humaniseOptimizationSlug(slug) || source;
  }
  return SOURCE_DISPLAY_NAMES[source] || titleCaseSource(source);
}
